import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { SCREENS } from '../navigation/screens';

interface Props {
  readonly rolePrefix: string;
  readonly mainRoutes: readonly string[];
}

/**
 * In-shell back/title bar.
 *
 * The native stack header renders ABOVE the whole screen (and thus above the
 * NavBanner toolbar). We disable it for owner/user screens and render this bar
 * instead, INSIDE the shell and BELOW the NavBanner — so the back header always
 * sits under the normal navigation toolbar.
 *
 * Shown on sub-pages only, using the same main-vs-sub logic as NavBanner
 * (derived from the registered route name, e.g. "owner/subscriptions/gifts/new").
 */
export default function SubHeader({ rolePrefix, mainRoutes }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const navigation = useNavigation<any>();
  const route = useRoute();

  // Use the route's PATH (not name) for main-vs-sub detection, matching
  // NavBanner: index routes have a 3-part name (owner/fleet/index) but a
  // 2-part path (owner/fleet) and must read as a main tab, not a sub-page.
  const entry = SCREENS.find((s) => s.name === route.name);
  const parts = (entry?.path ?? route.name).split('/');
  if (parts[0] !== rolePrefix) return null;
  const currentSegment = parts[1] ?? 'index';
  const isSubPage = !mainRoutes.includes(currentSegment) || parts.length > 2;
  if (!isSubPage) return null;

  const title = entry?.title ?? '';

  const goBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
  };

  return (
    <View style={styles.bar}>
      <TouchableOpacity onPress={goBack} hitSlop={8} style={styles.backBtn} accessibilityRole="button" accessibilityLabel="Back">
        <Ionicons name="chevron-back" size={22} color={colors.primary} />
        <Text style={styles.backText}>Back</Text>
      </TouchableOpacity>
      {title ? <Text style={styles.title} numberOfLines={1}>{title}</Text> : null}
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.borderLight,
    backgroundColor: c.surface,
  },
  backBtn: { flexDirection: 'row', alignItems: 'center' },
  backText: { fontSize: fontSize.md, color: c.primary, fontWeight: fontWeight.medium as any },
  title: { fontSize: fontSize.md, color: c.text, fontWeight: fontWeight.semibold as any, marginLeft: spacing.sm, flexShrink: 1 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
